import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const UIContext = createContext(null);

export function UIProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState({ isOpen: false, message: '', resolve: null });
  const counterRef = useRef(0);

  /**
   * showNotification(message, type)             — legacy signature
   * showNotification({ message, type, action }) — extended: optional { label, onClick }
   * Returns the toast id so callers can dismiss it programmatically.
   */
  const showNotification = useCallback((arg1, type = 'success') => {
    const id = ++counterRef.current;
    let toast;
    if (typeof arg1 === 'object' && arg1 !== null) {
      toast = { id, message: arg1.message, type: arg1.type || 'success', action: arg1.action };
    } else {
      toast = { id, message: arg1, type };
    }
    setToasts(prev => [...prev.slice(-3), toast]); // max 4 visible
    const ttl = toast.type === 'achievement' ? 5000 : toast.action ? 6000 : 3500;
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), ttl);
    return id;
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const confirm = useCallback((message) => {
    return new Promise((resolve) => {
      setConfirmState({ isOpen: true, message, resolve });
    });
  }, []);

  const handleConfirmResponse = (value) => {
    if (confirmState.resolve) confirmState.resolve(value);
    setConfirmState({ isOpen: false, message: '', resolve: null });
  };

  return (
    <UIContext.Provider value={{ showNotification, confirm, dismissToast }}>
      {children}

      {/* Toast stack */}
      <div className="toast-stack">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`toast-item toast-${toast.type}${toast.action ? ' toast-with-action' : ''}`}
            onClick={(e) => {
              if (e.target.closest('.toast-action-btn')) return;
              dismissToast(toast.id);
            }}
          >
            {toast.type === 'achievement' ? (
              <>
                <span className="toast-achieve-icon">{toast.message.split(' ')[0]}</span>
                <div className="toast-achieve-body">
                  <span className="toast-achieve-label">¡Logro Desbloqueado!</span>
                  <span className="toast-achieve-title">{toast.message.split('"')[1] ?? toast.message}</span>
                </div>
                <span className="toast-achieve-star">⭐</span>
              </>
            ) : (
              <>
                <span className="toast-icon">
                  {toast.type === 'success' ? '✅' : toast.type === 'warning' ? '⚠️' : toast.type === 'error' ? '❌' : 'ℹ️'}
                </span>
                <span className="toast-msg">{toast.message}</span>
                {toast.action && (
                  <button
                    className="toast-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      toast.action.onClick?.();
                      dismissToast(toast.id);
                    }}
                  >
                    {toast.action.label}
                  </button>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Confirm modal */}
      {confirmState.isOpen && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.7)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: 'var(--bg-secondary)', padding: '24px', borderRadius: '16px',
            maxWidth: '400px', width: '90%', border: '1px solid var(--border-color)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)', animation: 'slideUp 0.3s ease-out'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '1.2rem', color: 'var(--text-primary)' }}>Confirmar acción</h3>
            <p style={{ margin: '0 0 24px 0', color: 'var(--text-muted)', lineHeight: '1.5' }}>
              {confirmState.message}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => handleConfirmResponse(false)}>Cancelar</button>
              <button className="btn btn-gold" onClick={() => handleConfirmResponse(true)}>Confirmar</button>
            </div>
          </div>
        </div>
      )}
    </UIContext.Provider>
  );
}

export function useUI() {
  const context = useContext(UIContext);
  if (!context) throw new Error('useUI must be used within a UIProvider');
  return context;
}
